import { useMemo } from 'react'
import styled from 'styled-components'
import { Outlet } from 'react-router-dom'
import Watermark from 'antd-watermark'
import { Header } from './header'
import { MenuList } from './menuList'
import { Suspense, useState, useEffect } from 'react'
import moment from 'moment'
import { useFavicon, useTitle } from 'ahooks'
import Scrollbars from 'react-custom-scrollbars'
import { useLocation } from 'react-router-dom'
import { useGetLogo, useGetMenuList, useUserInfo } from '@/pages/login/util'
import { useGetSecurityConfig } from '@/pages/account-center/account-security/utils'
import { useGetSystemStatus } from '@/pages/account-center/system-upgrade/utils'
import { setGlobalLoading } from '@/redux/uploadSlice'
import { useDispatch } from 'react-redux'
import { useFullscreen } from '@/contexts/FullscreenContext'
import { t } from '@/i18n'

/**
 * 菜单别名枚举
 * @enum {string}
 * @property {string} overview - 态势概览
 * @property {string} validation - 攻击模拟
 * @property {string} script - 攻击编排
 * @property {string} configure - 配置中心
 * @property {string} report - 报表管理
 * @property {string} system - 系统管理
 * @property {string} useCase - 用例知识库
 * @property {string} account - 账号中心
 * @property {string} 'operation-management' - 运维管理
 */

enum MenuType {
  'phish' = 1,
  'web-gateway' = 2,
  'email-gateway' = 3,
  'waf' = 4,
  'threat-protection' = 5,
  'phish-group' = 6,
  'data-protection' = 7,
  'external-attack-surface-management' = 8,
  'endpoint-security' = 9,
  'orchestration' = 10,
  'container-security' = 11,
}

// 需要显示侧边栏菜单的路径前缀
const MENU_PATHS = [
  '/overview',
  '/validation',
  '/script',
  '/configure',
  '/report',
  '/system',
  '/useCase',
  '/account',
]

export const AdminLayout = () => {
  // 重点当前页面为loading过渡页，所以要判断是否是子账号登录，如果是子账号登录的话，主账号相关的请求需要做一个判断
  const dispatch = useDispatch()
  const [getMenuList, menuList] = useGetMenuList()
  const [getLogo, logo] = useGetLogo()
  const loginType = localStorage.getItem('loginType')
  const [watermarkcontent, setWatermarkcontent] = useState<any>()
  const [getSecurityConfig] = useGetSecurityConfig()
  const [userInfo, getUserInfo] = useUserInfo()
  const accountType = localStorage.getItem('account_type')
  const { pathname } = useLocation()
  const roleType = localStorage.getItem('roleType')
  useTitle(t('突破与攻击模拟系统'))
  useFavicon('/favicon.ico')
  const [getSystemStatus] = useGetSystemStatus()
  const [type, setType] = useState()
  const [superSU, setSuperSU] = useState<any>()
  // topMenu 目前通过 handelTopMenu 被设置，但未直接读取（已改用路径匹配判断是否显示菜单）
  // 保留此状态以便未来可能的扩展使用
  const [topMenu, setTopMenu] = useState('')
  const { isFullscreen } = useFullscreen()

  const Logo = useMemo(() => {
    let img = ''
    let fileType = ''
    if (logo?.home_logo_file_path?.file_name) {
      fileType = logo.home_logo_file_path.file_name.substring(
        logo.home_logo_file_path.file_name.lastIndexOf('.') + 1,
      )
      switch (fileType) {
        case 'png':
          img = `data:image/png;base64,${logo?.home_logo_file_path.base64}`
          break
        case 'jpg':
        case 'jpeg':
          img = `data:image/jpeg;base64,${logo?.home_logo_file_path.base64}`
          break
        case 'gif':
          img = `data:image/gif;base64,${logo?.home_logo_file_path.base64}`
          break
        case 'svg':
          img = `data:image/svg+xml;base64,${logo?.home_logo_file_path.base64}`
          break
        case 'ico':
          img = `data:image/x-icon;base64,${logo?.home_logo_file_path.base64}`
          break
        case 'bmp':
          img = `data:image/bmp;base64,${logo?.home_logo_file_path.base64}`
          break
        default:
          break
      }
    }
    return img
  }, [logo])
  useEffect(() => {
    let timer
    getLogo().catch(_ => {})
    if (loginType === 'sub-account' || accountType === '2') {
      if (roleType === '审计员') {
        window.location.href = `${location.protocol}//${location.hostname}/easm/log`
        return
      }
      const port = location.port ? `:${location.port}` : ''
      timer = setTimeout(() => {
        window.location.href =
          process.env.NODE_ENV === 'dev'
            ? `http://127.0.0.1:10/easm/overview`
            : `${location.protocol}//${location.hostname}${port}/easm/overview`
      }, 100)
    } else {
      getMenuList()
        .then(res => {
          let obj = {}
          let auth = {
            trail: [],
            official: [],
            outgoing: [],
          }
          res?.data?.items.map(item => {
            obj[item.alias] = item.checked
            if (item.checked === 1) {
              auth.outgoing.push(item.alias as never)
            }
            if (item.auth_type === 1) {
              auth.official.push(item.alias as never)
            } else if (item.auth_type === 2) {
              auth.trail.push(item.alias as never)
            }
          })
          localStorage.setItem('auth', JSON.stringify(auth))
          setSuperSU(obj)
          for (const key in obj) {
            if (obj[key] === 1 && key !== 'external-attack-surface-management') {
              setType(MenuType[key])
              return
            }
          }
        })
        .catch(_ => {})

      if (menuList.privatize && menuList.items.find(item => item.id === '12').checked === 1) {
        getSystemStatus()
          .then(res => {
            const { isUpgrading } = res.data
            dispatch(setGlobalLoading(isUpgrading === '1' ? true : false))
          })
          .catch(_ => {})
      }
    }
    return () => clearTimeout(timer)
  }, [loginType, accountType, roleType])

  useEffect(() => {
    if (loginType === 'sub-account') return
    getSecurityConfig()
      .then(res => {
        if (res?.data?.watermarkStatus === 'WatermarkStatusEnable') {
          if (res?.data?.watermarkContent) {
            setWatermarkcontent(res?.data?.watermarkContent)
          } else {
            const name = userInfo?.email ? userInfo.email.split('@')[0] : ''
            const net = userInfo?.email ? userInfo.email.split('@')[1] : ''
            setWatermarkcontent(`${name} ${net} ${moment().format('YYYYMMDD')}`)
          }
          setWatermarkcontent(res?.data?.watermarkContent)
        } else {
          setWatermarkcontent('')
        }
      })
      .catch(_ => {})
    getUserInfo().catch(_ => {})
  }, [])

  const handelTopMenu = e => {
    setTopMenu(e)
  }

  return (
    <AdminLayoutContainer>
      {!isFullscreen && (
        <Header
          userInfo={userInfo}
          superSU={superSU}
          menuList={menuList}
          type={type}
          Logo={Logo}
          handelTopMenu={handelTopMenu}
        />
      )}
      <div style={{ display: 'flex', flex: 1 }}>
        {!isFullscreen && MENU_PATHS.some(path => pathname.indexOf(path) > -1) && <MenuList />}
        <AdminLayoutContent>
          <Scrollbars>
            <Watermark content={watermarkcontent} style={{ height: '100%' }}>
              <Suspense fallback={<div>loading...</div>}>
                <Outlet />
              </Suspense>
            </Watermark>
          </Scrollbars>
        </AdminLayoutContent>
      </div>
    </AdminLayoutContainer>
  )
}

const AdminLayoutContainer = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
`
const AdminLayoutContent = styled.div`
  flex: 1;
`
